'use client';
import React, { useState } from 'react';
import {
  Sparkles,
  CheckCircle2,
  Copy,
  MessageCircle,
  Instagram,
  RefreshCw,
} from 'lucide-react';

export const TTOSSimulatorV3: React.FC = () => {
  const [industry, setIndustry] = useState('Cafe & Dining');
  const [goal, setGoal] = useState('Get 50+ weekend table reservations');
  const [channel, setChannel] = useState('WhatsApp + Instagram');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = () => {
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
    }, 600);
  };

  return (
    <section id="simulator" className="py-20 bg-slate-50 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-2 mb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold">
            <Sparkles className="w-3.5 h-3.5" />
            Live Strategy Simulator
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            See the &ldquo;Don&apos;t Prompt. Just Choose&rdquo; Magic Live
          </h2>
          <p className="text-sm text-slate-500">
            Pick your parameters below. Watch TT OS architect your entire multi-channel marketing run in real time.
          </p>
        </div>

        {/* Simulator Interactive Box */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-xl">
          
          {/* Controls Column */}
          <div className="lg:col-span-5 space-y-5">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
              1. Choose Your Parameters
            </h3>

            {/* Select Industry */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Business Category:</label>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 bg-slate-50 font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option>Cafe &amp; Dining</option>
                <option>Dental &amp; Health Clinic</option>
                <option>Real Estate Agency</option>
                <option>D2C Fashion Store</option>
                <option>EdTech &amp; Coaching</option>
              </select>
            </div>

            {/* Select Goal */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Campaign Objective:</label>
              <select
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                className="w-full p-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 bg-slate-50 font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option>Get 50+ weekend table reservations</option>
                <option>Acquire 100 high-intent consultation inquiries</option>
                <option>Flash Sale: 20% off festival stock</option>
                <option>Rank on Google My Business Local 3-Pack</option>
              </select>
            </div>

            {/* Select Channels */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Primary Channels:</label>
              <div className="grid grid-cols-2 gap-2">
                {['WhatsApp + Instagram', 'Google Maps + Web', 'Meta Ads + SMS', 'All Omnichannel'].map((ch) => (
                  <button
                    key={ch}
                    onClick={() => setChannel(ch)}
                    className={`p-2 rounded-xl text-xs font-semibold border text-center transition-all ${
                      channel === ch
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {ch}
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Action Button */}
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full py-3 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold text-xs shadow-md flex items-center justify-center gap-2 transition-transform active:scale-98"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Synthesizing Strategy with AI...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Generate Instant Campaign Blueprint</span>
                </>
              )}
            </button>
          </div>

          {/* Real-Time Generated Output Column */}
          <div className="lg:col-span-7 bg-slate-900 text-white rounded-2xl p-5 sm:p-6 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800 text-xs">
              <span className="flex items-center gap-2 font-bold text-emerald-400">
                <CheckCircle2 className="w-4 h-4" />
                Autonomous Plan Ready (0 Prompts Needed)
              </span>
              <span className="text-[10px] text-slate-400 font-mono">Status: Verified</span>
            </div>

            <div className="space-y-3 font-sans text-xs">
              <div className="p-3 bg-slate-800/80 rounded-xl border border-slate-700 space-y-1">
                <span className="text-[10px] text-purple-400 font-bold uppercase">Target: {industry}</span>
                <h4 className="text-sm font-bold text-white">&ldquo;{goal}&rdquo;</h4>
                <p className="text-slate-400 text-[11px]">Deploying via {channel}</p>
              </div>

              {/* Step Output 1: WhatsApp Broadcast Message */}
              <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/80 space-y-1.5">
                <div className="flex items-center justify-between text-[11px] text-emerald-400 font-bold">
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp Interactive Template
                  </span>
                  <span className="text-[10px] text-slate-400">Estimated Open Rate: 92%</span>
                </div>
                <div className="bg-slate-950 p-2.5 rounded-lg font-mono text-[11px] text-slate-300 border border-slate-800">
                  &ldquo;Namaste [First Name]! 🌟 Craving our chef specials this Friday? Book your VIP table in 1-click &amp; enjoy a complimentary dessert. Tap [Reserve Table] below!&rdquo;
                </div>
              </div>

              {/* Step Output 2: Social Media Caption & Visual Prompt */}
              <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/80 space-y-1.5">
                <div className="flex items-center justify-between text-[11px] text-pink-400 font-bold">
                  <span className="flex items-center gap-1">
                    <Instagram className="w-3.5 h-3.5" />
                    Instagram Reel &amp; Audio Pacing
                  </span>
                  <span className="text-[10px] text-slate-400">Scheduled: Friday 6:45 PM</span>
                </div>
                <p className="text-slate-300 text-[11px]">
                  <strong>Audio Hook:</strong> Trending lo-fi sitar beat (120 BPM) • <strong>Hook:</strong> &ldquo;The only monsoon platter you need to try in town...&rdquo; • <strong>Hashtags:</strong> #WeekendVibes #FoodieGram #PuneFood
                </p>
              </div>
            </div>

            <div className="pt-2 flex items-center justify-between text-xs">
              <span className="text-[11px] text-slate-400">Ready to execute with 1 click in your dashboard.</span>
              <button
                onClick={() => alert('Strategy copied to clipboard!')}
                className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold flex items-center gap-1.5 text-xs transition-colors"
              >
                <Copy className="w-3.5 h-3.5" />
                <span>Copy Blueprint</span>
              </button>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};

export default TTOSSimulatorV3;
